import { action, atom } from '@reatom/framework'

import { localStorage } from '@blade/std-lib'
import { STORAGE_PREFIX } from '@root/shared'

import type { WalletId } from '@root/entities/wallet'

/** Which half of the screen is showing: the money, or the wallets holding it. */
export type PortfolioTab = 'portfolio' | 'wallets'

/** Which of the three lists is under the panels. */
export type PortfolioList = 'positions' | 'holdings' | 'history'

/** Dollars, or the chain's own coin. */
export type PortfolioUnit = 'usd' | 'native'

const KEY = `${STORAGE_PREFIX}/v1/pulse-v2/portfolio`

type Kept = {
  tab: PortfolioTab
  list: PortfolioList
  unit: PortfolioUnit
}

const DEFAULTS: Kept = { tab: 'portfolio', list: 'positions', unit: 'usd' }

const TABS: ReadonlyArray<PortfolioTab> = ['portfolio', 'wallets']
const LISTS: ReadonlyArray<PortfolioList> = ['positions', 'holdings', 'history']
const UNITS: ReadonlyArray<PortfolioUnit> = ['usd', 'native']

/**
 * What was kept from last time, read one field at a time.
 *
 * A stored view that has gone stale — an older build's list name, a hand-edited
 * value, a half-written string — gives up only the field it cannot read. The
 * board's display settings learned this the same way: refusing the whole
 * object put a reader back to defaults because one word changed.
 */
export const parseView = (raw: string | undefined): Kept => {
  if (!raw) {
    return DEFAULTS
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return DEFAULTS
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return DEFAULTS
  }

  const read = <T extends string>(
    field: string,
    allowed: ReadonlyArray<T>,
    fallback: T,
  ): T => {
    const value = (parsed as Record<string, unknown>)[field]
    return typeof value === 'string' && allowed.includes(value as T)
      ? (value as T)
      : fallback
  }

  return {
    tab: read('tab', TABS, DEFAULTS.tab),
    list: read('list', LISTS, DEFAULTS.list),
    unit: read('unit', UNITS, DEFAULTS.unit),
  }
}

export const serialiseView = (view: Kept): string => JSON.stringify(view)

export const portfolioViewAtom = atom(
  parseView(localStorage.get(KEY)),
  'portfolioViewAtom',
)

const keep = (view: Kept) => {
  localStorage.set(KEY, serialiseView(view))
  return view
}

export const setPortfolioTabAction = action((ctx, tab: PortfolioTab) => {
  portfolioViewAtom(ctx, keep({ ...ctx.get(portfolioViewAtom), tab }))
}, 'setPortfolioTabAction')

export const setPortfolioListAction = action((ctx, list: PortfolioList) => {
  portfolioViewAtom(ctx, keep({ ...ctx.get(portfolioViewAtom), list }))
}, 'setPortfolioListAction')

export const togglePortfolioUnitAction = action((ctx) => {
  const view = ctx.get(portfolioViewAtom)
  portfolioViewAtom(
    ctx,
    keep({ ...view, unit: view.unit === 'usd' ? 'native' : 'usd' }),
  )
}, 'togglePortfolioUnitAction')

/**
 * Which wallets the figures are about.
 *
 * Not kept between visits on purpose: a wallet can be deleted, renamed or
 * imported somewhere else, and coming back to a screen whose numbers are about
 * a wallet you no longer recognise is worse than coming back to all of them.
 * `undefined` means every wallet, which is the question most people have.
 */
export const portfolioWalletAtom = atom<WalletId | undefined>(
  undefined,
  'portfolioWalletAtom',
)

export const pickPortfolioWalletAction = action(
  (ctx, walletId: WalletId | undefined) => {
    portfolioWalletAtom(ctx, walletId)
  },
  'pickPortfolioWalletAction',
)
